import { MAX_HEADING_LEVEL } from '@root/helpers/editor';
import { sumBy, uniq } from 'lodash-es';

import { HEADING_SIZE_RATIO } from '../../constants';
import { draftParagraphsOf, isBlankParagraph, mapDraftParagraphs } from '../draft/blocks';
import type { ClauseDraft, DraftParagraph, DraftRun } from '../draft/types';

const MIN_BODY_TEXT_SHARE = 0.5;
const MIN_TOP_HEADING_RATIO = 1.4;
const MAX_HEADING_SIZES = 2;

const visibleLengthOf = (run: DraftRun) => (run.kind === 'text' ? run.text.trim().length : 0);

const headingSizesOf = (
  paragraphs: DraftParagraph[],
  evidence: 'emphasis' | 'size',
  isCandidate: (sizePt: number) => boolean,
) =>
  uniq(
    paragraphs.flatMap(({ role }) =>
      role.kind === 'heading' && role.evidence.kind === evidence && isCandidate(role.evidence.sizePt)
        ? [role.evidence.sizePt]
        : [],
    ),
  ).sort((a, b) => b - a);

const sizeTiersOf = (paragraphs: DraftParagraph[], bodySizePt: number | undefined) => {
  const runs = paragraphs.flatMap(({ runs }) => runs);
  const bodyShare =
    sumBy(
      runs.filter((run) => run.format.sizePt === bodySizePt),
      visibleLengthOf,
    ) / Math.max(1, sumBy(runs, visibleLengthOf));
  if (bodySizePt === undefined || bodyShare < MIN_BODY_TEXT_SHARE) return new Map<number, number>();
  return new Map(
    headingSizesOf(paragraphs, 'size', (sizePt) => sizePt >= bodySizePt * HEADING_SIZE_RATIO)
      .slice(0, MAX_HEADING_SIZES)
      .map((sizePt) => [sizePt, sizePt >= bodySizePt * MIN_TOP_HEADING_RATIO ? 1 : 2]),
  );
};

export const promoteHeadings = (draft: ClauseDraft): ClauseDraft => {
  const { bodySizePt } = draft;
  const isOversized = (sizePt: number) => bodySizePt !== undefined && sizePt > bodySizePt;
  const paragraphs = draftParagraphsOf(draft.blocks);
  const emphasisSizes = headingSizesOf(paragraphs, 'emphasis', isOversized);
  const sizeTiers = sizeTiersOf(paragraphs, bodySizePt);

  const promote = (block: DraftParagraph): DraftParagraph => {
    const { role } = block;
    if (role.kind === 'body') return block;
    if (isBlankParagraph(block)) return { ...block, role: { kind: 'body' } };
    if (role.evidence.kind === 'style') return block;
    if (role.evidence.kind === 'size') {
      const level = sizeTiers.get(role.evidence.sizePt);
      return { ...block, role: level ? { ...role, level } : { kind: 'body' } };
    }
    if (!isOversized(role.evidence.sizePt)) return { ...block, role: { kind: 'body' } };
    return {
      ...block,
      role: { ...role, level: Math.min(MAX_HEADING_LEVEL, emphasisSizes.indexOf(role.evidence.sizePt) + 1) },
      runs: block.runs.map((run) => ({ ...run, format: { ...run.format, bold: false } })),
    };
  };

  return { ...draft, blocks: mapDraftParagraphs(draft.blocks, promote) };
};
